// CFD section — FE-037 / spec §7.0.11 + Dashboard-Filters-01.
//
// CFD owns three widget-level filters:
//   - range (7d / 30d / 90d / YTD)
//   - single-assignee picker (CFD endpoint quirk: it takes a single
//     ``assignee`` param, not ``assignee_filter[]``)
//   - tri-state blocked toggle (blocked / unblocked / all)
//
// Categories are inherited from the dashboard header.

import { useState } from "react";

import { useMembersPickerQuery } from "@/features/commitments/hooks";
import { SearchableSelect } from "@/features/commitments/SearchableSelect";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useCfdQuery,
  useCommonFilters,
  type CfdRangeLabel,
  type CfdResponse,
} from "@/features/dashboard";

import { CfdBottleneckBanner } from "./CfdBottleneckBanner";
import { CfdChart } from "./CfdChart";
import { CfdLegend } from "./CfdLegend";
import { CFD_STATUS_ORDER } from "./statusMeta";


export const CFD_SECTION = "cfd" as const;
export const CFD_SPAN_CLASS = "xl:col-span-3 md:col-span-2";


const RANGE_OPTIONS: ReadonlyArray<{
  value: CfdRangeLabel;
  label: string;
}> = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "ytd", label: "YTD" },
];

const DEFAULT_RANGE: CfdRangeLabel = "30d";

type BlockedFilter = "all" | "blocked" | "unblocked";


export function CfdSection() {
  const [range, setRange] = useState<CfdRangeLabel>(DEFAULT_RANGE);
  const [assignee, setAssignee] = useState<string>("");
  const [blockedFilter, setBlockedFilter] = useState<BlockedFilter>("all");
  const filters = useCommonFilters();

  // The CFD endpoint accepts a single ``assignee`` and a boolean
  // ``blocked``. ``blocked=true`` shows blocked-only; we synthesize
  // ``unblocked`` client-side by leaving the param off (BE returns
  // every commitment) and noting it's a known approximation.
  // ACQ-008 — the header office / Account selection rides alongside the
  // widget filters so the chart matches the other widgets.
  const query = useCfdQuery({
    range,
    category: filters.category,
    assignee: assignee || undefined,
    blocked: blockedFilter === "blocked" ? true : undefined,
    clientFilter: filters.clientFilter,
    officeFilter: filters.officeFilter,
  });

  return (
    <DashboardCard
      section={CFD_SECTION}
      className={CFD_SPAN_CLASS}
      title="Cumulative flow"
      subtitle="Commitments by workflow status over time."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <CfdAssigneePicker value={assignee} onChange={setAssignee} />
          <CfdBlockedToggle value={blockedFilter} onChange={setBlockedFilter} />
          <RangeSelector value={range} onChange={setRange} />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* The banner shares the chart's office / Account slice so it never
            describes the whole book next to a filtered chart (ACQ-008). */}
        <CfdBottleneckBanner
          clientFilter={filters.clientFilter}
          officeFilter={filters.officeFilter}
        />
        {query.isLoading ? (
          <DashboardLoadingState variant="card" />
        ) : query.isError ? (
          <DashboardErrorState
            title="Couldn't load Cumulative flow"
            retry={() => query.refetch()}
          />
        ) : query.data && hasAnyData(query.data) ? (
          <>
            <CfdChart
              data={query.data}
              category={
                filters.category && filters.category.length === 1
                  ? filters.category[0]
                  : undefined
              }
              assignee={assignee || undefined}
            />
            <CfdLegend />
          </>
        ) : (
          <DashboardEmptyState
            title="No Commitment flow data in this range"
            description="Once Commitments transition through the workflow, the cumulative flow appears here."
          />
        )}
      </div>
    </DashboardCard>
  );
}


function hasAnyData(data: CfdResponse): boolean {
  if (data.series.length === 0) return false;
  for (const status of CFD_STATUS_ORDER) {
    if ((data.total_by_status[status] ?? 0) > 0) return true;
    for (const point of data.series) {
      if ((point.counts[status] ?? 0) > 0) return true;
    }
  }
  return false;
}


function CfdAssigneePicker({
  value, onChange,
}: { value: string; onChange: (next: string) => void }) {
  const membersQuery = useMembersPickerQuery();
  const options = [
    { value: "", label: "All assignees", isClear: true },
    ...(membersQuery.data?.members ?? []).map((m) => ({
      value: m.id,
      label: m.display_name || m.email,
      hint: m.email,
    })),
  ];
  const current = membersQuery.data?.members.find((m) => m.id === value);
  return (
    <span
      data-testid="cfd-assignee-picker"
      data-value={value || "all"}
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised px-1 py-0.5"
    >
      <SearchableSelect
        fieldId="cfd-assignee"
        value={value}
        options={options}
        triggerDisplay={
          <span className="text-text-primary">
            {current
              ? `Assignee: ${current.display_name || current.email}`
              : "Assignee — all"}
          </span>
        }
        onPick={(next) => onChange(next)}
      />
    </span>
  );
}


function CfdBlockedToggle({
  value, onChange,
}: { value: BlockedFilter; onChange: (next: BlockedFilter) => void }) {
  const items: Array<{ value: BlockedFilter; label: string }> = [
    { value: "all", label: "All" },
    { value: "blocked", label: "Blocked" },
    { value: "unblocked", label: "Unblocked" },
  ];
  return (
    <div
      data-testid="cfd-blocked-toggle"
      data-value={value}
      role="group"
      aria-label="Blocked filter"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            aria-pressed={active}
            data-testid={`cfd-blocked-${item.value}`}
            data-active={active ? "true" : "false"}
            onClick={() => onChange(item.value)}
            className={[
              "rounded px-2 py-0.5 text-[11px] font-medium transition-fast ease-out",
              active
                ? "bg-teal-tint text-text-primary"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}


function RangeSelector({
  value, onChange,
}: { value: CfdRangeLabel; onChange: (next: CfdRangeLabel) => void }) {
  return (
    <div
      data-testid="cfd-range-selector"
      role="group"
      aria-label="Cumulative flow range"
      className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-raised p-1"
    >
      {RANGE_OPTIONS.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            data-active={active ? "true" : "false"}
            data-testid={`cfd-range-${opt.value}`}
            className={[
              "rounded px-2.5 py-1 text-xs font-medium transition-fast ease-out",
              active
                ? "bg-teal-tint text-text-primary"
                : "text-text-secondary hover:text-text-primary",
            ].join(" ")}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
